import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/alert';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/form';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/select';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/sheet';

import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import {
  useFieldArray,
  useForm,
  useFormContext,
  useWatch,
} from 'react-hook-form';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@Components/AlertDialog';
import { useCreateInviteCode } from '@Hooks/useCreateInviteCode';
import { useInviteCodePreview } from '@Hooks/useInviteCodePreview';
import { useInviteCodeUsage } from '@Hooks/useInviteCodeUsage';
import { useSubscriptionTiers } from '@Hooks/useSubscriptionTiers';
import { useUpdateInviteCode } from '@Hooks/useUpdateInviteCode';
import type {
  PreviewRegistrationCodePayload,
  RegistrationCode,
  RegistrationCodePayload,
  RegistrationCodeToastMiss,
} from '@Types/registrationCode';
import { MultiSelectCombobox } from '../MultiSelectCombobox';
import InviteCodePreviewPanel from './InviteCodePreviewPanel';
import {
  CODE_PATTERN,
  DISCOUNT_KIND_LABELS,
  DISCOUNT_KINDS,
  InviteCodeFormSchema,
  type InviteCodeFormValues,
  InviteCodePreviewSchema,
  needsAmount,
  parseAmount,
  pickChangedFields,
  toInviteCodeFormValues,
  toInviteCodePayload,
  touchesRenewalFields,
  WINDOW_KIND_LABELS,
  WINDOW_KINDS,
  WINDOW_UNIT_LABELS,
  WINDOW_UNITS,
} from './inviteCodeForm';

const TOAST_TARGET_LABELS: Record<RegistrationCodeToastMiss['target'], string> =
  {
    subscription: 'membership fee',
    wallet: 'wallet deposit',
  };

type DiscountFieldName =
  | 'signupFee'
  | 'signupWallet'
  | 'renewalFee'
  | 'renewalWallet';

type WindowFieldName = 'renewalFee' | 'renewalWallet' | 'renewalCredit';

const readToastMisses = (error: unknown): RegistrationCodeToastMiss[] => {
  const toastMisses = (
    error as { details?: { toastMisses?: unknown } } | null
  )?.details?.toastMisses;
  return Array.isArray(toastMisses) ? toastMisses : [];
};

function WindowFields({ name }: { name: WindowFieldName }) {
  const { control } = useFormContext<InviteCodeFormValues>();
  const windowKind = useWatch({ control, name: `${name}.windowKind` });

  return (
    <div className="flex flex-wrap items-start gap-2">
      <FormField
        control={control}
        name={`${name}.windowKind`}
        render={({ field }) => (
          <FormItem>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="w-60">
                  <SelectValue />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {WINDOW_KINDS.map((kind) => (
                  <SelectItem key={kind} value={kind}>
                    {WINDOW_KIND_LABELS[kind]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormItem>
        )}
      />
      {windowKind === 'LIMITED' && (
        <>
          <FormField
            control={control}
            name={`${name}.windowAmount`}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    step={1}
                    className="w-24"
                    aria-label="Window length"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name={`${name}.windowUnit`}
            render={({ field }) => (
              <FormItem>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className="w-32" aria-label="Window unit">
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {WINDOW_UNITS.map((unit) => (
                      <SelectItem key={unit} value={unit}>
                        {WINDOW_UNIT_LABELS[unit]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />
          <span className="pt-2 text-sm text-zinc-500">after joining</span>
        </>
      )}
    </div>
  );
}

function DiscountField({
  name,
  label,
  description,
  windowName,
}: {
  name: DiscountFieldName;
  label: string;
  description: string;
  windowName?: WindowFieldName;
}) {
  const { control } = useFormContext<InviteCodeFormValues>();
  const kind = useWatch({ control, name: `${name}.kind` });

  return (
    <div className="space-y-2">
      <FormLabel>{label}</FormLabel>
      <div className="flex flex-wrap items-start gap-2">
        <FormField
          control={control}
          name={`${name}.kind`}
          render={({ field }) => (
            <FormItem>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger className="w-40" aria-label={label}>
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {DISCOUNT_KINDS.map((discountKind) => (
                    <SelectItem key={discountKind} value={discountKind}>
                      {DISCOUNT_KIND_LABELS[discountKind]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormItem>
          )}
        />
        {needsAmount(kind) && (
          <FormField
            control={control}
            name={`${name}.amount`}
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center gap-1">
                  {kind !== 'PERCENT' && (
                    <span className="text-sm text-zinc-500">$</span>
                  )}
                  <FormControl>
                    <Input
                      type="number"
                      inputMode="decimal"
                      min={0}
                      step="0.01"
                      className="w-32"
                      aria-label={`${label} amount`}
                      {...field}
                    />
                  </FormControl>
                  {kind === 'PERCENT' && (
                    <span className="text-sm text-zinc-500">%</span>
                  )}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </div>
      {windowName && kind !== 'NONE' && <WindowFields name={windowName} />}
      <p className="text-muted-foreground text-sm">{description}</p>
    </div>
  );
}

function CreditField({
  name,
  label,
  description,
}: {
  name: 'signupCredit' | 'renewalCredit';
  label: string;
  description: string;
}) {
  const { control } = useFormContext<InviteCodeFormValues>();
  const amount = useWatch({ control, name: `${name}.amount` });
  const hasCredit = !!parseAmount(amount);

  return (
    <div className="space-y-2">
      <FormField
        control={control}
        name={`${name}.amount`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{label}</FormLabel>
            <div className="flex items-center gap-1">
              <span className="text-sm text-zinc-500">$</span>
              <FormControl>
                <Input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  placeholder="0"
                  className="w-32"
                  {...field}
                />
              </FormControl>
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
      {name === 'renewalCredit' && hasCredit && (
        <WindowFields name="renewalCredit" />
      )}
      <p className="text-muted-foreground text-sm">{description}</p>
    </div>
  );
}

function PromoDetailsField() {
  const { control } = useFormContext<InviteCodeFormValues>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'promoDetails',
  });

  return (
    <div className="space-y-2">
      <FormLabel>Promo details</FormLabel>
      {fields.map((item, index) => (
        <div key={item.id} className="flex items-center gap-2">
          <FormField
            control={control}
            name={`promoDetails.${index}.value`}
            render={({ field }) => (
              <FormItem className="grow">
                <FormControl>
                  <Input
                    placeholder="e.g. Annual fee waived"
                    aria-label={`Promo detail ${index + 1}`}
                    {...field}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label={`Remove promo detail ${index + 1}`}
            onClick={() => remove(index)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => append({ value: '' })}
      >
        <Plus className="mr-2 h-4 w-4" />
        Add line
      </Button>
      <p className="text-muted-foreground text-sm">
        Shown to prospects when they enter the code.
      </p>
    </div>
  );
}

export interface InviteCodeSheetProps {
  inviteCode?: RegistrationCode | null;
  isOpen: boolean;
  setIsOpen: (newIsOpen: boolean) => void;
  onSuccess?: () => void;
}

export default function InviteCodeSheet({
  inviteCode,
  isOpen,
  setIsOpen,
  onSuccess,
}: InviteCodeSheetProps) {
  const isEditMode = !!inviteCode;
  const [toastMisses, setToastMisses] = useState<RegistrationCodeToastMiss[]>(
    [],
  );
  const [pendingChanges, setPendingChanges] =
    useState<Partial<RegistrationCodePayload> | null>(null);

  const createMutation = useCreateInviteCode();
  const updateMutation = useUpdateInviteCode();

  const tiers = useSubscriptionTiers({ enabled: isOpen });
  const usage = useInviteCodeUsage({
    id: inviteCode?.id ?? '',
    enabled: isOpen && isEditMode,
  });
  const activeSubscriptionCount = usage.data?.activeSubscriptionCount ?? 0;

  const tierItems = useMemo(
    () =>
      (tiers.data ?? []).map((tier) => ({
        value: tier.id,
        label: tier.title,
      })),
    [tiers.data],
  );

  const baselinePayload = useMemo(
    () =>
      inviteCode ? toInviteCodePayload(toInviteCodeFormValues(inviteCode)) : null,
    [inviteCode],
  );

  const form = useForm<InviteCodeFormValues>({
    resolver: zodResolver(InviteCodeFormSchema),
    defaultValues: toInviteCodeFormValues(inviteCode),
  });

  useEffect(() => {
    if (isOpen) {
      form.reset(toInviteCodeFormValues(inviteCode));
      setToastMisses([]);
    }
  }, [inviteCode, isOpen, form]);

  const watchedValues = useWatch({ control: form.control });
  const previewPayload = useMemo<PreviewRegistrationCodePayload | null>(() => {
    const parsed = InviteCodePreviewSchema.safeParse(watchedValues);
    if (!parsed.success) return null;
    const { code, ...payload } = toInviteCodePayload(parsed.data);
    return CODE_PATTERN.test(code) ? { ...payload, code } : payload;
  }, [watchedValues]);

  const preview = useInviteCodePreview({
    payload: previewPayload,
    enabled: isOpen,
  });

  const save = async (request: () => Promise<RegistrationCode>) => {
    form.clearErrors('root');
    setToastMisses([]);

    try {
      await request();
      onSuccess?.();
      setIsOpen(false);
    } catch (error) {
      setToastMisses(readToastMisses(error));
      form.setError('root', {
        type: 'server',
        message:
          error instanceof Error
            ? error.message
            : 'We were unable to save this Invite Code. Please check your changes and try again.',
      });
    }
  };

  const onSubmit = async (values: InviteCodeFormValues) => {
    const availableTierIds = new Set(tierItems.map((item) => item.value));
    const payload = toInviteCodePayload({
      ...values,
      allowedSubTierIds: values.allowedSubTierIds.filter((id) =>
        availableTierIds.has(id),
      ),
    });

    if (payload.allowedSubTierIds.length === 0) {
      form.setError('allowedSubTierIds', {
        type: 'manual',
        message: 'Select at least one tier.',
      });
      return;
    }

    if (!inviteCode || !baselinePayload) {
      await save(() => createMutation.mutateAsync(payload));
      return;
    }

    const changes = pickChangedFields(payload, baselinePayload);
    if (Object.keys(changes).length === 0) {
      setIsOpen(false);
      return;
    }

    if (activeSubscriptionCount > 0 && touchesRenewalFields(changes)) {
      setPendingChanges(changes);
      return;
    }

    await save(() =>
      updateMutation.mutateAsync({ id: inviteCode.id, payload: changes }),
    );
  };

  const handleConfirmRenewalChanges = async () => {
    const changes = pendingChanges;
    setPendingChanges(null);
    if (!inviteCode || !changes) return;
    await save(() =>
      updateMutation.mutateAsync({ id: inviteCode.id, payload: changes }),
    );
  };

  const handleOnOpenChange = (newIsSheetOpen: boolean) => {
    setIsOpen(newIsSheetOpen);

    if (!newIsSheetOpen) {
      form.reset();
      setToastMisses([]);
    }
  };

  const rootError = form.formState.errors.root?.message;
  const isSubmitting =
    form.formState.isSubmitting ||
    createMutation.isPending ||
    updateMutation.isPending;

  return (
    <Sheet open={isOpen} onOpenChange={handleOnOpenChange}>
      <SheetContent
        className="w-full sm:max-w-5xl"
        onPointerDownOutside={(e) => e.preventDefault()}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="h-full">
            <div className="flex h-full flex-col">
              <SheetHeader>
                <SheetTitle>
                  {isEditMode ? `Edit ${inviteCode.code}` : 'Create Invite Code'}
                </SheetTitle>
              </SheetHeader>

              <SheetBody className="grow overflow-auto">
                {rootError && (
                  <div className="mb-4">
                    <Alert variant="destructive">
                      <AlertTitle>Something went wrong</AlertTitle>
                      <AlertDescription>
                        {toastMisses.length > 0 ? (
                          <div>
                            <p>No matching Toast discount. Create these in Toast first:</p>
                            <ul className="mt-1 list-disc pl-5">
                              {toastMisses.map((miss) => (
                                <li key={`${miss.tierId}-${miss.target}`}>
                                  {miss.tierTitle},{' '}
                                  {TOAST_TARGET_LABELS[miss.target]}:{' '}
                                  {miss.expected}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ) : (
                          rootError
                        )}
                      </AlertDescription>
                    </Alert>
                  </div>
                )}

                {isEditMode && activeSubscriptionCount > 0 && (
                  <div className="mb-4">
                    <Alert>
                      <AlertTitle>
                        {activeSubscriptionCount} active{' '}
                        {activeSubscriptionCount === 1 ? 'member uses' : 'members use'}{' '}
                        this code
                      </AlertTitle>
                      <AlertDescription>
                        Renewal changes apply to them at their next renewal.
                      </AlertDescription>
                    </Alert>
                  </div>
                )}

                <div className="grid gap-8 py-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]">
                  <div className="space-y-8">
                    <section className="space-y-6">
                      <h3 className="font-semibold">Basics</h3>
                      <FormField
                        control={form.control}
                        name="code"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Code</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="e.g. STORK25"
                                {...field}
                                onChange={(e) =>
                                  field.onChange(e.target.value.toUpperCase())
                                }
                              />
                            </FormControl>
                            <p className="text-muted-foreground text-sm">
                              Prospects type this into the invitation code
                              box. It cannot match a partner code.
                            </p>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <PromoDetailsField />

                      <FormField
                        control={form.control}
                        name="allowedSubTierIds"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Allowed tiers</FormLabel>
                            <MultiSelectCombobox
                              items={tierItems}
                              selectedValues={field.value ?? []}
                              onChange={field.onChange}
                              placeholder="Select tiers..."
                              disabled={tiers.isLoading}
                              loading={tiers.isLoading}
                              inModal
                            />
                            <p className="text-muted-foreground text-sm">
                              Prospects using this code can join these tiers.
                            </p>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </section>

                    <section className="space-y-6">
                      <h3 className="font-semibold">At signup</h3>
                      <DiscountField
                        name="signupFee"
                        label="Membership fee discount"
                        description="Needs a matching Toast discount."
                      />
                      <DiscountField
                        name="signupWallet"
                        label="Wallet deposit discount"
                        description="Needs a matching Toast discount."
                      />
                      <CreditField
                        name="signupCredit"
                        label="Extra wallet credit"
                        description="Added to the wallet at signup on top of the deposit. Use a negative amount to credit less."
                      />
                    </section>

                    <section className="space-y-6">
                      <h3 className="font-semibold">At renewal</h3>
                      <DiscountField
                        name="renewalFee"
                        label="Membership fee discount"
                        description="Applied to membership renewals."
                        windowName="renewalFee"
                      />
                      <DiscountField
                        name="renewalWallet"
                        label="Wallet renewal discount"
                        description="Applied to each wallet renewal charge."
                        windowName="renewalWallet"
                      />
                      <CreditField
                        name="renewalCredit"
                        label="Wallet credit adjustment per renewal"
                        description="Added to the wallet at each wallet renewal on top of the renewal amount. Use a negative amount to credit less."
                      />
                    </section>
                  </div>

                  <div className="lg:sticky lg:top-0 lg:self-start">
                    <InviteCodePreviewPanel
                      preview={preview.data}
                      isUpdating={previewPayload !== null && preview.isUpdating}
                      error={preview.error}
                      isFormValid={previewPayload !== null}
                    />
                  </div>
                </div>
              </SheetBody>

              <SheetFooter>
                <Button
                  type="submit"
                  variant="primary"
                  loading={isSubmitting}
                  disabled={tiers.isLoading || usage.isLoading}
                >
                  {isEditMode ? 'Update' : 'Create'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => handleOnOpenChange(false)}
                >
                  Cancel
                </Button>
              </SheetFooter>
            </div>
          </form>
        </Form>
      </SheetContent>

      <AlertDialog
        open={pendingChanges !== null}
        onOpenChange={(open) => {
          if (!open) setPendingChanges(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change renewal terms?</AlertDialogTitle>
            <AlertDialogDescription>
              {activeSubscriptionCount}{' '}
              {activeSubscriptionCount === 1 ? 'member uses' : 'members use'}{' '}
              this code. Renewal changes apply to them at their next renewal.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmRenewalChanges}>
              Save changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
}
